// "Forgot password?" link used on the login + change-password screens.
// No self-service reset by design — these accounts are bound to an
// admin-driven flow (admin runs the reset script). Clicking the link
// opens a modal that explains what to do, lightly. Modal closes on
// Esc, scrim click, or the dismiss button.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "../i18n";
import "../styles/forgot-password.css";

interface Props {
    className?: string;
}

const ForgotPasswordButton = ({ className = "" }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    // Esc closes the modal — matches the rest of the admin modal UX.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Rendered at document.body via a portal so the overlay's
    // position:fixed actually covers the viewport. Without the portal
    // it'd be contained by any transformed ancestor (the login form
    // applies a translateX during the colour→pin step animation, with
    // animation-fill-mode: both, so the transform persists and creates
    // a containing block for fixed-position descendants).
    const modal = (
        <div
            className="forgot-password-overlay"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
        >
            <div
                className="forgot-password-card"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="forgot-password-close"
                    onClick={() => setOpen(false)}
                    aria-label={t("close")}
                >
                    <X size={18} />
                </button>
                <h2 id="forgot-password-title" className="forgot-password-title">
                    {t("forgot_password_title")}
                </h2>
                <p className="forgot-password-body">
                    {t("forgot_password_body")}
                </p>
                <button
                    type="button"
                    className="btn btn-primary forgot-password-dismiss"
                    onClick={() => setOpen(false)}
                    autoFocus
                >
                    {t("forgot_password_dismiss")}
                </button>
            </div>
        </div>
    );

    return (
        <>
            <button
                type="button"
                className={`forgot-password-link ${className}`.trim()}
                onClick={() => setOpen(true)}
            >
                {t("forgot_password")}
            </button>

            {open && createPortal(modal, document.body)}
        </>
    );
};

export default ForgotPasswordButton;
